import { Routes } from '@/src/common/enums/routes'
import type {
  AutoPayReviewComponentType,
  AutoPayViewScreenProps,
} from '@/src/experiences/card-payments/screens/AutoPayReview'
import { graphql } from '@api/mesa'
import { useMutation } from '@apollo/client'
import { CardPaymentsGql } from '@/src/experiences/card-payments/gql'
import { GET_INCOMING_SCHEDULED_TRANSFERS } from '@/src/experiences/card-activity/components/feed/_highnote/ScheduledTransfers'
import { useAlertPaymentProcessingError } from '@/src/app/main/(tabs)/card/card-management/payment/_useAlertPaymentProcessingError'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useLegacyAutoPayStore } from '@/src/experiences/card-payments/store/useLegacyAutoPayStore'

export function LegacyAutoPayReview({
  AutoPayReviewComponent,
  autoPayViewScreenProps,
}: {
  AutoPayReviewComponent: AutoPayReviewComponentType
  autoPayViewScreenProps: AutoPayViewScreenProps
}) {
  const alertPaymentProcessingError = useAlertPaymentProcessingError()
  const logError = useLogError()

  const [createRecurringPayment, { loading }] = useMutation(
    graphql(`
      mutation createRecurringPayment(
        $highnoteExternalAccountId: String!
        $transferAmountStrategy: TransferBalanceAmountCode!
        $transferDayOfMonth: Int!
        $consentTimestamp: String!
      ) {
        createRecurringPaymentV2(
          highnoteExternalAccountId: $highnoteExternalAccountId
          transferAmountStrategy: $transferAmountStrategy
          transferDayOfMonth: $transferDayOfMonth
          consentTimestamp: $consentTimestamp
        ) {
          __typename
          ... on MesaUserError {
            message
          }
        }
      }
    `),
    {
      refetchQueries: [
        CardPaymentsGql.Queries.GET_RECURRING_SCHEDULED_PAYMENTS,
        GET_INCOMING_SCHEDULED_TRANSFERS,
      ],
      async onQueryUpdated(observableQuery) {
        await new Promise((resolve) => setTimeout(resolve, 888))
        await observableQuery.refetch()
      },
      onCompleted({ createRecurringPaymentV2 }) {
        createRecurringPaymentV2.__typename === 'MesaUserError'
          ? alertPaymentProcessingError(createRecurringPaymentV2.message)
          : autoPayViewScreenProps.routerPush(Routes.CardManagementAutoPaySuccessful.route)
      },
      onError(error) {
        logError(error)
        alertPaymentProcessingError()
      },
    },
  )

  const { account, day, paymentStrategy } = useLegacyAutoPayStore()

  return (
    <AutoPayReviewComponent
      loading={loading}
      account={account}
      day={day ? day.label : undefined}
      paymentStrategy={paymentStrategy ? paymentStrategy.label : undefined}
      createRecurringPayment={() => {
        if (!account?.highnoteExternalAccountId || !paymentStrategy?.value || !day?.value) {
          alertPaymentProcessingError()
          return
        }

        void createRecurringPayment({
          variables: {
            highnoteExternalAccountId: account.highnoteExternalAccountId,
            transferAmountStrategy: paymentStrategy.value,
            transferDayOfMonth: day.value,
            consentTimestamp: new Date().toISOString(),
          },
        })
      }}
      autoPayViewScreenProps={autoPayViewScreenProps}
    />
  )
}
